import React from 'react';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import {makeStyles} from '@material-ui/core/styles';

// The RAG service emits LaTeX using its native \( \) / \[ \] delimiters,
// but remark-math only recognises $ $ / $$ $$ — convert before parsing.
// (Only the delimiter tokens are touched; backslashes inside the math
// itself, e.g. \frac, \cdot, are left untouched.)
//
// remark-math also silently refuses to parse a $ / $$ fence that has a
// space or tab directly inside it (e.g. "$ x $"), to avoid colliding with
// plain currency text — but the RAG service pads its fences with spaces
// (both "$ x $" and "$$ x $$"), so every equation was being left as raw
// text. Trim that inner padding so the fences are valid for remark-math.
const normalizeMathDelimiters = (text: string): string =>
  text
    .replace(/\\\[([\s\S]*?)\\\]/g, (_match, expr) => `$$${expr.trim()}$$`)
    .replace(/\\\(([\s\S]*?)\\\)/g, (_match, expr) => `$${expr.trim()}$`)
    .replace(/\$\$([\s\S]*?)\$\$/g, (_match, expr) => `$$${expr.trim()}$$`)
    .replace(
      /(?<!\$)\$(?!\$)(?!\d)([^$\n]*?)\$(?!\$)/g,
      (_match, expr) => `$${expr.trim()}$`,
    );

const useStyles = makeStyles((theme) => ({
  root: {
    '& > *:first-child': {
      marginTop: 0,
    },
    '& > *:last-child': {
      marginBottom: 0,
    },
    '& p': {
      margin: theme.spacing(0, 0, 1),
    },
    '& ul, & ol': {
      margin: theme.spacing(0, 0, 1),
      paddingLeft: theme.spacing(3),
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: theme.spacing(1.5, 0, 1),
      fontWeight: 600,
    },
    '& code': {
      backgroundColor: theme.palette.action.hover,
      borderRadius: 4,
      padding: '1px 4px',
      fontSize: '0.9em',
    },
    '& pre': {
      backgroundColor: theme.palette.action.hover,
      borderRadius: 6,
      padding: theme.spacing(1.5),
      overflowX: 'auto',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
      },
    },
    '& blockquote': {
      margin: theme.spacing(0, 0, 1),
      paddingLeft: theme.spacing(1.5),
      borderLeft: `3px solid ${theme.palette.divider}`,
      color: theme.palette.text.secondary,
    },
    '& table': {
      borderCollapse: 'collapse',
      marginBottom: theme.spacing(1),
    },
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(0.5, 1),
    },
    '& a': {
      wordBreak: 'break-all',
    },
    '& .katex-display': {
      margin: theme.spacing(1, 0),
      overflowX: 'auto',
      overflowY: 'hidden',
    },
  },
}));

interface MarkdownMessageProps {
  content: string;
}

// Renders LLM/RAG responses (bold, headers, lists, tables, code blocks) as
// actual formatted markdown instead of raw asterisks/hashes.
const MarkdownMessage: React.FC<MarkdownMessageProps> = ({content}) => {
  const classes = useStyles();
  return (
    <div className={classes.root}>
      <ReactMarkdown
        remarkPlugins={[gfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        linkTarget='_blank'>
        {normalizeMathDelimiters(content)}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownMessage;
